import { uploadBenefitImage } from '@ben/columbia/api';
import Banner from '@ben/orion/banner';
import Button from '@ben/orion/deprecated-button';
import FileUploader from '@ben/orion/file-uploader';
import ImageCropper from '@ben/orion/image-cropper';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModalRef } from '@ben/shared/components/modal/modal';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { convertUrlToFile } from '@ben/shared/utils/convert-url-into-file';
import { useMutation } from '@tanstack/react-query';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css, useTheme } from 'styled-components';
import DeleteConfirmationModal from '../shared/delete-confirmation-modal';
import useSetDirtyValue from '../hooks/use-set-dirty-value';
import {
  ButtonsWrapper,
  FileUploadDescriptionContainer,
  FileUploadTitleWrapper,
  FileUploadWrapper,
  VerticalDivider,
} from '../shared/file-upload.styles';

interface IModbenBenefitImageProps {
  isCloneBenefit: boolean;
  setIsUploadInprogress: Dispatch<SetStateAction<boolean>>;
}

const ModbenBenefitImage = ({
  isCloneBenefit,
  setIsUploadInprogress,
}: IModbenBenefitImageProps) => {
  const { control, getFieldState } = useFormContext<IModularBenefitDetail>();
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const theme = useTheme();
  const deleteImageConfirmationModalRef = useRef<IModalRef>(null);

  const benefitImage = useWatch({ control, name: 'benefitImage', exact: true });

  const isImageUploadInProgress = useMemo(
    () =>
      benefitImage?.image !== undefined &&
      typeof benefitImage?.image !== 'string',
    [benefitImage]
  );

  const isBenefitImageDirty = getFieldState('benefitImage').isDirty;
  const isPersistedToBenefit = useMemo(() => {
    return typeof benefitImage?.image === 'string' && !isBenefitImageDirty;
  }, [benefitImage?.image, isBenefitImageDirty]);

  useEffect(() => {
    setIsUploadInprogress(isImageUploadInProgress);
  }, [isImageUploadInProgress, setIsUploadInprogress]);

  const uploadBenefitImageMutation = useMutation(
    (data: unknown) =>
      uploadBenefitImage(data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    {
      onSuccess: (data) => {
        setDirtyValue('benefitImage', {
          ...data,
          benefit: undefined,
        });
        toast.info(
          'Image uploaded successfully, but please save the benefit to reflect the changes.'
        );
      },
      onError: () => {
        toast.error('Error uploading image, please try again.');
      },
    }
  );

  useEffect(() => {
    if (!isCloneBenefit || !benefitImage) return;

    const uploadBenefitImage = async () => {
      const file = await convertUrlToFile({
        url: benefitImage?.image as string,
      });
      uploadBenefitImageMutation.mutate({
        image: file,
      });
    };

    void uploadBenefitImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloneBenefit]);

  return (
    <>
      <FileUploadWrapper $inProgress={isImageUploadInProgress}>
        <FileUploadTitleWrapper>
          <Typography
            variant={TypographyVariant.h2}
            color={
              isImageUploadInProgress
                ? theme.palette.error.main
                : theme.palette.neutral[100]
            }
          >
            Benefit image
          </Typography>
          <FileUploadDescriptionContainer>
            <Typography variant={TypographyVariant.bodyMd1}>
              Upload an image or logo to allow users to identify the benefit.
            </Typography>
            <Typography variant={TypographyVariant.bodyMd1}>
              Recommended 250px x 250px
            </Typography>
          </FileUploadDescriptionContainer>
        </FileUploadTitleWrapper>
        {benefitImage ? (
          <>
            {isImageUploadInProgress ? (
              <Banner
                variant="critical"
                message="You haven’t saved the image. Your changes will be lost if you don’t save them."
                hideIcon
              />
            ) : undefined}
            <BenefitImageContainer>
              <ImageCropper
                src={benefitImage?.image as string}
                onCropComplete={setCroppedImage}
              />
              <ButtonsWrapper>
                <InlineTextButton
                  label="Delete image"
                  onClick={() => {
                    deleteImageConfirmationModalRef.current?.showModal();
                  }}
                />
                {isImageUploadInProgress ? (
                  <>
                    <VerticalDivider />
                    <Button
                      label="Save image"
                      onClick={() => {
                        uploadBenefitImageMutation.mutate({
                          image: croppedImage,
                        });
                      }}
                      width="122px"
                    />
                  </>
                ) : undefined}
              </ButtonsWrapper>
            </BenefitImageContainer>
          </>
        ) : (
          <FileUploader
            uploading={false}
            onFileUpload={(file) => {
              setDirtyValue('benefitImage', {
                id: 0,
                image: file[0],
                imageAltText: '',
              });
            }}
            acceptedFileExtensions={['png', 'jpg', 'jpeg']}
          />
        )}
        <Input
          name="Benefit image alt text (optional)"
          variant={InputVariant.TextArea}
          maxLength={250}
          value={benefitImage?.imageAltText}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setDirtyValue('benefitImage.imageAltText', event.target.value);
          }}
          width="100%"
          hintText="The benefit image alt text describes your image or gif and gives context to users who may have trouble seeing your image. You can only change this if you have saved the benefit image to the benefit."
          disabled={!isPersistedToBenefit || isImageUploadInProgress}
        />
      </FileUploadWrapper>
      <DeleteConfirmationModal
        ref={deleteImageConfirmationModalRef}
        title="Delete benefit image"
        message="Are you sure you want to delete the Benefit image?"
        onConfirm={() => {
          setDirtyValue('benefitImage', undefined);
        }}
      />
    </>
  );
};

export default memo(ModbenBenefitImage);

const BenefitImageContainer = styled(FileUploadDescriptionContainer)`
  ${({ theme }) => css`
    width: 320px;
    align-items: flex-end;
  `}
`;
